import cx from 'classnames';
import * as _ from 'lodash';
import { useMemo, type VoidFunctionComponent } from 'react';
import ContentLoader from 'react-content-loader';

import Locator from '../../../../shared/Locator';
import { useDialogContext } from '../../../contexts/DialogContext';
import { useQuery } from '../../../contexts/RouterContext';
import { useAddToast } from '../../../contexts/ToastContext';
import { useIssueExceptionById } from '../../../hooks/api/issues';
import { ButtonGroup, Icon, icons } from '../../generic';
import Button from '../../generic/Button';
import { DetailsPanelList } from '../../generic/DetailsPanel';
import { DynamicShowMoreText } from '../../generic/DynamicShowMoreText/DynamicShowMoreText';
import FallbackImage from '../../generic/FallbackImage';
import { FromNowWithTooltip } from '../../generic/FromNow';
import Tabs from '../../generic/Tabs';
import type { IssueExceptionsForDashboard, IssueExceptionsRemoveRequest } from '../../../../shared/issuesV2';
import type { SetState } from '../../../types';
import type { DetailsPanelListRowProps } from '../../generic/DetailsPanel/DetailsPanelListRow';

import IssueExceptionsTableIgnoreScopeBadge from './IssueExceptionsTableIgnoreScopeBadge';
import { canRemoveException } from './permissions';
import { getHumanReadableVersion } from './utils';

import styles from './IssueExceptionsDrawer.module.less';

type IssueExceptionsQueryParams = {
  selectedExceptionId?: number;
};

type Props = {
  onOpenExtendExceptionModal: SetState<IssueExceptionsForDashboard | null>;
  onRemoveExceptions: (removeCriteria: IssueExceptionsRemoveRequest) => Promise<number>;
};

const IssueExceptionsDrawerLoading = (
  <ContentLoader width="100%" height="600px">
    <rect x="0" y="0" rx="3" ry="3" width="100" height="14" />
    <rect x="0" y="20" rx="3" ry="3" width="70%" height="28" />
    <rect x="0" y="56" rx="3" ry="3" width="140" height="12" />
    <rect x="0" y="80" rx="4" ry="4" width="100" height="32" />
    <rect x="110" y="80" rx="4" ry="4" width="120" height="32" />
    <rect x="0" y="140" rx="3" ry="3" width="60" height="18" />
    <rect x="0" y="165" rx="1" ry="1" width="100%" height="2" />
    <rect x="0" y="190" rx="3" ry="3" width="70" height="14" />
    <rect x="70%" y="190" rx="3" ry="3" width="25%" height="14" />
    <rect x="0" y="225" rx="3" ry="3" width="70" height="14" />
    <rect x="70%" y="225" rx="3" ry="3" width="25%" height="14" />
    <rect x="0" y="260" rx="3" ry="3" width="100" height="14" />
    <rect x="70%" y="260" rx="3" ry="3" width="25%" height="14" />
    <rect x="0" y="295" rx="3" ry="3" width="120" height="14" />
    <rect x="70%" y="295" rx="3" ry="3" width="25%" height="14" />
    <rect x="0" y="330" rx="3" ry="3" width="80" height="14" />
    <rect x="70%" y="330" rx="3" ry="3" width="25%" height="14" />
    <rect x="0" y="365" rx="3" ry="3" width="50" height="14" />
    <rect x="70%" y="365" rx="3" ry="3" width="25%" height="14" />
  </ContentLoader>
);

//  Fallback for when an issue exception is not found.
const IssueExceptionsDrawerNotFound = (
  <FallbackImage src="no-data" alt="No data found" hasPadding>
    Could not find this issue ignore rule.
  </FallbackImage>
);

/**
 * Renders a drawer for a specific exception.
 * Note that because this is managed by the useDresser hook, this is
 * technically just the "contents" of the drawer and not a Drawer component itself.
 */
const IssueExceptionsDrawer: VoidFunctionComponent<Props> = ({ onOpenExtendExceptionModal, onRemoveExceptions }) => {
  const [queryParams] = useQuery<IssueExceptionsQueryParams>();
  const { selectedExceptionId } = queryParams;
  const { onConfirm } = useDialogContext();
  const addToast = useAddToast();

  //  Fetch a single exception by its ID.
  const { data, isLoading, isError, error } = useIssueExceptionById(
    selectedExceptionId as number,
    !!selectedExceptionId,
  );

  //  Build the rows for the details panel
  const detailRows = useMemo(() => {
    const rows: DetailsPanelListRowProps[] = [];

    if (!data) {
      return rows;
    }

    //  Only add package information if the exception is specific to a package
    if (data.dependencyRevisionLocator) {
      const dependencyRevisionLocator = Locator.parse(data.dependencyRevisionLocator);

      rows.push({
        label: 'Package',
        content: dependencyRevisionLocator.package,
      });

      if (data.packageScope) {
        rows.push({
          label: 'Version',
          content: getHumanReadableVersion(data.packageScope, dependencyRevisionLocator),
        });
      }
    }

    //  Add ignore scope badge
    rows.push({
      label: 'Ignore Scope',
      content: (
        <IssueExceptionsTableIgnoreScopeBadge
          ignoreScope={data.ignoreScope}
          projectTitle={data.projectTitle}
          policyTitle={data.policyTitle}
          shouldOutlineBadge
        />
      ),
    });

    //  Add package label
    rows.push({
      label: 'Package Label',
      content: data.packageLabel || '-',
    });

    //  Add created by
    rows.push({
      label: 'Created By',
      content: data.createdBy,
    });

    //  Add creation date
    rows.push({
      label: 'Created',
      content: <FromNowWithTooltip date={data.createdAt} />,
    });

    //  Add note
    rows.push({
      label: 'Note',
      content: (
        <span className="italic">
          <DynamicShowMoreText text={data.note || 'No Reason Provided'} shouldUnsetFont />
        </span>
      ),
    });

    return rows;
  }, [data]);

  //  Render fallback if there is an error.
  if (isError) {
    //  Show the not found UI instead of the error-plug if it's one of our known not found errors.
    if (error.data?.name === 'NotFoundError') {
      return IssueExceptionsDrawerNotFound;
    }

    return (
      <FallbackImage src="error-plug" alt="Error fetching issue" hasPadding>
        {error.data?.message ?? (error as unknown as Error)?.message ?? 'An unknown error occurred'}
      </FallbackImage>
    );
  }

  //  Render the loading state.
  if (isLoading) {
    return IssueExceptionsDrawerLoading;
  }

  //  Render fallback if no data is found, and we are not loading
  if (!data) {
    return IssueExceptionsDrawerNotFound;
  }

  //  These are the same check.
  const canRemove = canRemoveException(data);
  const canExtend = canRemove;

  const handleRemoveException = () => {
    onConfirm({
      title: 'Remove Ignore Rule',
      body: 'Are you sure you want to remove this issue ignore rule?',
      onConfirm: async () => {
        await onRemoveExceptions({ kind: 'idList', value: [data.id] });
        addToast({
          type: 'success',
          content: `Rule "${data.exceptionTitle}" removed.`,
        });
      },
      allowEnterConfirm: true,
    });
  };

  return (
    <>
      <header className={styles.header}>
        <h2 className={styles.headerTitle}>
          {data.exceptionTitle}
        </h2>
        {data?.expiresAfter && (
          <div className={cx(styles.expiresAfter, { [styles.expiresAfterHasActions]: canRemove || canExtend })}>
            <Icon icon={icons.calendarO} />
            <span className={styles.expiresAfterText}>
              {data.isExpired ? 'Expired' : 'Expires'}
              {' '}
              <span className={styles.expiresAfterTextBold}>
                <FromNowWithTooltip date={data.expiresAfter} />
              </span>
            </span>
          </div>
        )}
        {(canRemove || canExtend) && (
          <div className="margin-bottom-sm">
            <ButtonGroup>
              {canExtend && (
                <Button
                  type="secondary"
                  size="medium"
                  icon={icons.alarmSnoozeO}
                  onClick={() => onOpenExtendExceptionModal(data)}
                >
                  {data?.expiresAfter ? 'Extend Rule' : 'Set Expiration'}
                </Button>
              )}
              {canRemove && (
                <Button
                  type="error"
                  size="medium"
                  icon={icons.trashRegular}
                  onClick={handleRemoveException}
                >
                  Remove Rule
                </Button>
              )}
            </ButtonGroup>
          </div>
        )}
      </header>
      <Tabs>
        <Tabs.Tab label="Details" onSelect={_.noop} isActive />
      </Tabs>
      <DetailsPanelList rows={detailRows} />
    </>
  );
};

export default IssueExceptionsDrawer;
